import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, EmptyState, DeadlineChip } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

function AssignedComplaintsInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => { fetchComplaints(); }, []);

  const fetchComplaints = async () => {
    try {
      const res = await api.get('/authority/assigned-complaints');
      setComplaints(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar collapsed={false} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="Assigned to Me"
            subtitle={`${complaints.length} active task${complaints.length !== 1 ? 's' : ''}`}
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/dashboard')}>Dashboard</button>
            }
          />
          <div style={s.content}>
            <PageHeader
              icon="📋"
              title="Assigned to Me"
              subtitle="Complaints escalated to you for resolution"
              stats={[
                { label: 'Total Assigned', value: complaints.length },
                { label: 'Overdue', value: complaints.filter(c => c.is_overdue).length },
              ]}
            />
            {error && <div style={s.alertErr}>{error}</div>}
            {!complaints.length ? (
              <EmptyState text="No assigned complaints" sub="Nothing escalated to you yet" />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 16 }}>
                {complaints.map((c, i) => (
                  <div
                    key={c.id}
                    className="ur-card-hover"
                    style={{
                      ...s.card,
                      cursor: 'pointer',
                      animationDelay: `${i * 0.05}s`,
                      borderLeft: c.status === 'escalated' ? `3px solid ${COLORS.red}` : c.is_overdue ? `3px solid ${COLORS.red}` : `3px solid ${COLORS.cyan}`,
                    }}
                    onClick={() => navigate(`/authority/resolve/${c.id}`)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: c.is_overdue ? '#fca5a5' : '#fff', flex: 1, marginRight: 10 }}>
                        {c.title}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                        {c.status === 'escalated' && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#fca5a5', background: 'rgba(239,68,68,0.2)', padding: '2px 8px', borderRadius: 10, border: '1px solid rgba(239,68,68,0.4)' }}>
                            ESCALATED
                          </span>
                        )}
                        {c.is_overdue && c.status !== 'escalated' && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#fca5a5', background: 'rgba(239,68,68,0.2)', padding: '2px 8px', borderRadius: 10, border: '1px solid rgba(239,68,68,0.4)' }}>
                            OVERDUE
                          </span>
                        )}
                        <DeadlineChip seconds={c.time_left_seconds} />
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginBottom: 12, lineHeight: 1.5 }}>
                      {c.description?.slice(0, 90)}{c.description?.length > 90 ? '...' : ''}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 8, marginBottom: 10 }}>
                      {[
                        ['Student', c.student_name],
                        [c.department ? 'Department' : 'Category', c.department || c.university_category || '—']
                      ].map(([label, val]) => (
                        <div key={label} style={{
                          padding: '8px 10px', borderRadius: 8,
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.12)',
                        }}>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 12, color: '#fff', fontWeight: 600 }}>{val}</div>
                        </div>
                      ))}
                    </div>
                    {c.resolution_deadline && (
                      <div style={{ fontSize: 11, color: COLORS.amber, marginBottom: 8 }}>
                        Deadline: {new Date(c.resolution_deadline && !c.resolution_deadline.endsWith('Z') ? c.resolution_deadline + 'Z' : c.resolution_deadline).toLocaleString()}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', textAlign: 'right', fontWeight: 600 }}>
                      Click to resolve
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AssignedComplaints() {
  return (
    <ThemeProvider>
      <AssignedComplaintsInner />
    </ThemeProvider>
  );
}
